import React from 'react';

import styled from 'styled-components';

import { LayoutArea } from '../types';

export interface LayoutAreaWrapperProps {
  /** Content to display */
  children: React.ReactNode;
  /** Custom className for styling */
  className?: string;
  /** Layout area type - determines automatic styling */
  area: LayoutArea;
}

/**
 * Automatic wrapper component that applies the correct styling based on LayoutArea.
 * This eliminates the need for manual styled divs in consuming components.
 *
 * Usage:
 * <LayoutAreaWrapper area={LayoutArea.HEADER}>
 *   <h1>My Header</h1>
 * </LayoutAreaWrapper>
 */
export const LayoutAreaWrapper: React.FC<LayoutAreaWrapperProps> = ({ children, className, area }) => {
  switch (area) {
    case LayoutArea.HEADER:
      return <HeaderContainer className={className}>{children}</HeaderContainer>;

    case LayoutArea.TABS:
      return <TabsContainer className={className}>{children}</TabsContainer>;

    case LayoutArea.BREADCRUMB:
      return <BreadcrumbContainer className={className}>{children}</BreadcrumbContainer>;

    case LayoutArea.ACTION_BUTTONS:
      return <ActionContainer className={className}>{children}</ActionContainer>;

    case LayoutArea.FOOTER:
      return <FooterContainer className={className}>{children}</FooterContainer>;

    case LayoutArea.CONTENT:
      return <ContentContainer className={className}>{children}</ContentContainer>;

    case LayoutArea.LEFT_SIDEBAR:
    case LayoutArea.RIGHT_SIDEBAR:
      return <SidebarContainer className={className}>{children}</SidebarContainer>;

    default:
      // For any other areas, use a generic container
      return <GenericContainer className={className}>{children}</GenericContainer>;
  }
};

// Styled components with automatic theme integration for each layout area

const HeaderContainer = styled.div`
  padding: ${({ theme }) => theme.space.spacing20};
  background: ${({ theme }) => theme.colors.palette.blues[0]};
  border-radius: ${({ theme }) => theme.radii.base};

  h1,
  h2,
  h3 {
    margin: 0 0 ${({ theme }) => theme.space.spacing10} 0;
    color: ${({ theme }) => theme.colors.textPrimary};
  }

  h1 {
    font-size: ${({ theme }) => theme.fontSizes.heading1};
  }

  h2 {
    font-size: ${({ theme }) => theme.fontSizes.heading2};
  }

  h3 {
    font-size: ${({ theme }) => theme.fontSizes.subheading};
  }

  p {
    margin: 0;
    font-size: ${({ theme }) => theme.fontSizes.footnote};
    color: ${({ theme }) => theme.colors.textSecondary};
  }

  @media (max-width: 768px) {
    padding: ${({ theme }) => theme.space.spacing10};
  }
`;

const TabsContainer = styled.div`
  display: flex;
  gap: ${({ theme }) => theme.space.spacing10};
  padding: ${({ theme }) => theme.space.spacing10} ${({ theme }) => theme.space.spacing20};
  overflow-x: auto;
  -webkit-overflow-scrolling: touch;

  @media (max-width: 768px) {
    padding: ${({ theme }) => theme.space.spacing10};
    gap: ${({ theme }) => theme.space.spacing10};
  }
`;

const BreadcrumbContainer = styled.div`
  padding: ${({ theme }) => theme.space.spacing10} ${({ theme }) => theme.space.spacing20};
  font-size: ${({ theme }) => theme.fontSizes.footnote};
  color: ${({ theme }) => theme.colors.textSecondary};

  span {
    &:hover {
      color: ${({ theme }) => theme.colors.primary};
      cursor: pointer;
    }
  }

  @media (max-width: 768px) {
    padding: ${({ theme }) => theme.space.spacing10};
  }
`;

const ActionContainer = styled.div`
  display: flex;
  gap: ${({ theme }) => theme.space.spacing10};
  padding: ${({ theme }) => theme.space.spacing10} ${({ theme }) => theme.space.spacing20};
  align-items: center;
  flex-wrap: wrap;

  @media (max-width: 768px) {
    padding: ${({ theme }) => theme.space.spacing10};
    gap: ${({ theme }) => theme.space.spacing10};

    /* Stack buttons on mobile */
    flex-direction: column;
    align-items: stretch;
  }
`;

const FooterContainer = styled.div`
  padding: ${({ theme }) => theme.space.spacing10} ${({ theme }) => theme.space.spacing20};
  background: ${({ theme }) => theme.colors.palette.grayscale[1]};
  border-top: 1px solid ${({ theme }) => theme.colors.borderBase};

  p {
    margin: 0;
    font-size: ${({ theme }) => theme.fontSizes.footnote};
    color: ${({ theme }) => theme.colors.textSecondary};
    text-align: center;
  }

  @media (max-width: 768px) {
    padding: ${({ theme }) => theme.space.spacing10};
  }
`;

const ContentContainer = styled.div`
  padding: ${({ theme }) => theme.space.spacing20};
  min-height: 0;
  overflow: auto;
  -webkit-overflow-scrolling: touch;
  display: flex;
  flex-direction: column;
  flex: 1 1 0;

  h1,
  h2,
  h3,
  h4,
  h5,
  h6 {
    margin: 0 0 ${({ theme }) => theme.space.spacing10} 0;
    color: ${({ theme }) => theme.colors.textPrimary};
  }

  h1 {
    font-size: ${({ theme }) => theme.fontSizes.heading1};
  }

  h2 {
    font-size: ${({ theme }) => theme.fontSizes.heading2};
  }

  h3 {
    font-size: ${({ theme }) => theme.fontSizes.subheading};
  }

  h4,
  h5,
  h6 {
    font-size: ${({ theme }) => theme.fontSizes.body};
  }

  p {
    margin: 0 0 ${({ theme }) => theme.space.spacing10} 0;
    font-size: ${({ theme }) => theme.fontSizes.body};
    color: ${({ theme }) => theme.colors.textPrimary};
    line-height: ${({ theme }) => theme.lineHeights.body};
  }

  @media (max-width: 768px) {
    padding: ${({ theme }) => theme.space.spacing10};
  }
`;

const SidebarContainer = styled.div`
  padding: ${({ theme }) => theme.space.spacing20};
  min-height: 0;
  overflow: auto;
  -webkit-overflow-scrolling: touch;
  display: flex;
  flex-direction: column;
  flex: 1 1 0;
  background: ${({ theme }) => theme.colors.palette.white};
  border-left: 1px solid ${({ theme }) => theme.colors.borderBase};

  h1,
  h2,
  h3,
  h4 {
    margin: 0 0 ${({ theme }) => theme.space.spacing10} 0;
    color: ${({ theme }) => theme.colors.textPrimary};
  }

  h2 {
    font-size: ${({ theme }) => theme.fontSizes.heading2};
  }

  h3 {
    font-size: ${({ theme }) => theme.fontSizes.subheading};
  }

  h4 {
    font-size: ${({ theme }) => theme.fontSizes.body};
  }

  p {
    margin: 0 0 ${({ theme }) => theme.space.spacing10} 0;
    font-size: ${({ theme }) => theme.fontSizes.body};
    color: ${({ theme }) => theme.colors.textPrimary};
    line-height: ${({ theme }) => theme.lineHeights.body};
  }

  @media (max-width: 768px) {
    padding: ${({ theme }) => theme.space.spacing10};
    border-left: none;
    border-top: 1px solid ${({ theme }) => theme.colors.borderBase};
  }
`;

const GenericContainer = styled.div`
  padding: ${({ theme }) => theme.space.spacing20};
  height: 100%;
  min-height: 0;
  display: flex;
  flex-direction: column;

  @media (max-width: 768px) {
    padding: ${({ theme }) => theme.space.spacing10};
  }
`;

// Convenience exports for specific layout areas
export const HeaderWrapper: React.FC<Omit<LayoutAreaWrapperProps, 'area'>> = (props) => <LayoutAreaWrapper {...props} area={LayoutArea.HEADER} />;

export const TabsWrapper: React.FC<Omit<LayoutAreaWrapperProps, 'area'>> = (props) => <LayoutAreaWrapper {...props} area={LayoutArea.TABS} />;

export const BreadcrumbWrapper: React.FC<Omit<LayoutAreaWrapperProps, 'area'>> = (props) => <LayoutAreaWrapper {...props} area={LayoutArea.BREADCRUMB} />;

export const ActionWrapper: React.FC<Omit<LayoutAreaWrapperProps, 'area'>> = (props) => <LayoutAreaWrapper {...props} area={LayoutArea.ACTION_BUTTONS} />;

export const FooterWrapper: React.FC<Omit<LayoutAreaWrapperProps, 'area'>> = (props) => <LayoutAreaWrapper {...props} area={LayoutArea.FOOTER} />;

export const ContentWrapper: React.FC<Omit<LayoutAreaWrapperProps, 'area'>> = (props) => <LayoutAreaWrapper {...props} area={LayoutArea.CONTENT} />;

export const SidebarWrapper: React.FC<Omit<LayoutAreaWrapperProps, 'area'> & { side?: 'left' | 'right' }> = ({ side = 'right', ...props }) => (
  <LayoutAreaWrapper {...props} area={side === 'left' ? LayoutArea.LEFT_SIDEBAR : LayoutArea.RIGHT_SIDEBAR} />
);
